import moment from "moment";
import { shiftRequest } from "../ShiftEarnings/shiftEarningsApi";
import { migratedOutstanding } from "./loansData";

// Payroll > Pay Inputs > Loans & Advances talks to the advance request APIs
// (TblAdvanceRequest), the same ones Team Workspace Approvals > Advance
// Salary and My Workspace use. The Shift Earnings request helper is reused
// (token, 401 / 409 handling, error text).
//
// Rule from the backend (Advance / Loan API doc, section 2): with advance
// type Salary only a Loan gets a repayment schedule; a Salary Advance has
// no repayment fields at all.

const REQUEST = "advance/request/";
const withCompany = (path, companyId, extra = {}) =>
  `${path}?${new URLSearchParams({ ...extra, company_id: companyId })}`;

const toDate = (value) => {
  const parsed = moment(value, ["YYYY-MM-DD", "DD-MM-YYYY", "DD/MM/YYYY", moment.ISO_8601], true);
  return parsed.isValid() ? parsed.format("YYYY-MM-DD") : value || "";
};
const toNumber = (value) => {
  const number = Number(value);
  return value === null || value === undefined || value === "" || !Number.isFinite(number) ? 0 : number;
};

// Statuses after which nothing is left to do on the request.
export const CLOSED_STATUSES = ["Closed", "Pre-Closed"];
export const ENDED_STATUSES = ["Closed", "Pre-Closed", "Cancelled", "Rejected"];

// API row -> list row. `type` is new (Loan / Advance); rows saved before it
// existed are read as Loan when they carry an instalment plan.
export const fromAdvanceApi = (row) => {
  const instalments = toNumber(row.no_of_installments ?? row.number_of_installments);
  const type = row.type || (row.repayment_mode === "Installment" || instalments > 1 ? "Loan" : "Advance");
  const amount = toNumber(row.effective_amount) || toNumber(row.amount);
  const paid = toNumber(row.paid_amount);
  const balance = row.balance_amount == null || row.balance_amount === "" ? Math.max(amount - paid, 0) : toNumber(row.balance_amount);
  const progress = amount > 0 ? Math.min(100, Math.round((paid / amount) * 100)) : 0;
  return {
    ...row,
    id: row.id ?? row.advance_id,
    user_id: row.user_id != null ? String(row.user_id) : "",
    issue_date: toDate(row.request_date || row.applied_at || row.created_at),
    employee_code: row.employee_id != null ? String(row.employee_id) : "",
    employee_name: row.user_name || row.employee_name || "",
    department: row.department || row.department_name || "",
    designation: row.designation || row.designation_name || "",
    image_url: row.image_url || "",
    record_type: type,
    advance_type: row.advance_type || "Salary",
    reason: row.reason || "",
    disbursed_amount: amount,
    repaid_amount: paid,
    outstanding_amount: balance,
    progress_pct: progress,
    show_progress_bar: type === "Loan",
    progress_label:
      type === "Loan"
        ? instalments > 1
          ? `${instalments} instalments · ${progress}% repaid`
          : `${progress}% repaid`
        : CLOSED_STATUSES.includes(row.status)
          ? "Recovered"
          : "No repayment schedule",
    instalment_count: instalments,
    repayment_mode: row.repayment_mode || "",
    repayment_date: toDate(row.repayment_date || row.repayment_start_date),
    repayment_frequency: row.repayment_frequency || "",
    description: row.description || "",
    payroll_month: toDate(row.payroll_month),
    attachment_url: row.attachment_url || (typeof row.attachment === "string" ? row.attachment : ""),
    status: row.status || "Pending",
    color_code: row.color_code || "",
  };
};

// Same query as the Payroll advance screen this replaced: every request the
// signed-in user can see in the company, without rejected ones.
export const listAdvanceRequests = async (domain, companyId, userId, signal) => {
  const data = await shiftRequest(domain, withCompany(REQUEST, companyId, { user_id: userId, exclude_status: "Rejected" }), { signal });
  const list = Array.isArray(data) ? data : data?.data ?? data?.results;
  if (!Array.isArray(list)) throw new Error("The server returned an invalid loans and advances list.");
  return list.map(fromAdvanceApi);
};

// One request with its repayment schedule ([{ due_date, amount_due,
// amount_paid, balance, status, payrun_label }]).
export const getAdvanceRequest = async (domain, companyId, id, signal) => {
  const data = await shiftRequest(domain, withCompany(`${REQUEST}${id}/`, companyId), { signal });
  const row = data?.data ?? data;
  if (!row || typeof row !== "object") throw new Error("The server returned an invalid request.");
  return {
    ...fromAdvanceApi(row),
    repayment_schedule: (Array.isArray(row.repayment_schedule) ? row.repayment_schedule : []).map((entry) => ({
      ...entry,
      due_date: toDate(entry.due_date),
      amount_due: toNumber(entry.amount_due),
      amount_paid: toNumber(entry.amount_paid),
      balance: toNumber(entry.balance),
    })),
  };
};

// Form -> single submission body (same fields as the import, keyed by the
// numeric user_id like the other advance screens). request_date is set on
// create only: the backend never changes it on edit.
export const toAdvancePayload = (form, { creating }) => {
  const isLoan = form.record_type === "Loan";
  const months = Number(form.months);
  return {
    user_id: Number(form.user_id),
    type: form.record_type,
    advance_type: "Salary",
    amount: Number(form.amount),
    reason: form.reason.trim(),
    description: (form.description || "").trim(),
    ...(creating ? { request_date: moment().format("YYYY-MM-DD") } : {}),
    ...(isLoan
      ? {
          repayment_mode: months >= 2 ? "Installment" : "Full",
          repayment_date: moment(form.start_month, "YYYY-MM").startOf("month").format("YYYY-MM-DD"),
          repayment_frequency: "Monthly",
          ...(months >= 2 ? { no_of_installments: months } : {}),
        }
      : {
          // Advance: recovered in full in the selected payroll month; the
          // repayment date is that month's last day (e.g. 2026-10-31).
          payroll_month: moment(form.payroll_month, "YYYY-MM").startOf("month").format("YYYY-MM-DD"),
          repayment_mode: "Full",
          repayment_date: moment(form.payroll_month, "YYYY-MM").endOf("month").format("YYYY-MM-DD"),
        }),
  };
};

// The backend doesn't take attachments yet: the upload box is shown disabled
// with a Coming Soon chip. Set to true once it does.
export const ATTACHMENT_AVAILABLE = false;

// Field names description / payroll_month / attachment are assumed (not yet
// checked against the backend). With a new file the request goes as
// multipart form data; without one it stays JSON.
export const saveAdvanceRequest = (domain, companyId, form, id, signal) => {
  const payload = toAdvancePayload(form, { creating: id == null });
  let body = payload;
  if (ATTACHMENT_AVAILABLE && form.attachment) {
    body = new FormData();
    Object.entries(payload).forEach(([key, value]) => {
      if (value !== undefined && value !== null) body.append(key, String(value));
    });
    body.append("attachment", form.attachment);
  }
  return shiftRequest(domain, withCompany(id == null ? REQUEST : `${REQUEST}${id}/`, companyId), {
    method: id == null ? "POST" : "PUT",
    body,
    signal,
  });
};

// ---- Approve / Reject (Loan & Advance Approve / Reject API) ----
// Only Pending requests. The caller needs "First Level Approve" on the
// advance submodule and can't action their own request (both answer 403;
// the server's message is shown).

// Approves a list row as requested: the amount and repayment terms the
// request already carries (the API only allows going down, never up).
export const toApprovePayload = (row, comments) => {
  const requested = toNumber(row.amount) || row.disbursed_amount;
  const mode = row.repayment_mode || (row.record_type === "Loan" && row.instalment_count > 1 ? "Installment" : "Full");
  return {
    approved_amount: requested,
    repayment_mode: mode,
    ...(mode === "Installment" ? { approved_installments: Math.max(row.instalment_count, 1) } : {}),
    ...(row.repayment_date ? { approved_repayment_date: row.repayment_date } : {}),
    ...(row.repayment_frequency ? { approved_repayment_frequency: row.repayment_frequency } : {}),
    ...(comments ? { approver_comments: comments } : {}),
  };
};

export const approveAdvanceRequest = (domain, companyId, row, comments, signal) =>
  shiftRequest(domain, withCompany(`${REQUEST}${row.id}/approve/`, companyId), { method: "PUT", body: toApprovePayload(row, comments), signal });

// rejection_reason is required; the answer has no record (data: null), so
// the list is reloaded afterwards.
export const rejectAdvanceRequest = (domain, companyId, id, reason, signal) =>
  shiftRequest(domain, withCompany(`${REQUEST}${id}/reject/`, companyId), { method: "PUT", body: { rejection_reason: reason }, signal });

export const cancelAdvanceRequest = (domain, companyId, id, reason, signal) =>
  shiftRequest(domain, withCompany(`${REQUEST}${id}/cancel/`, companyId), { method: "PUT", body: { reason }, signal });

// ---- Bulk import (Advance / Loan Bulk Import API) ----
// Each saved row is a new advance / loan request: it starts as Pending and
// goes to the employee's primary approver, same as POST advance/request/.
//
// records: [{ employee_id, type?, advance_type?, amount, reason,
//   request_date?, repayment_mode?, repayment_date?, repayment_frequency?,
//   no_of_installments?, purpose?, expected_expense_date? }]
// Answer: { total, success | valid, failed, rows: [{ index, employee_id,
//   employee_name, type, advance_type, status, message, amount?,
//   request_date? }] }, index = position in `records`. status is
//   "success" / "failed" (import) or "valid" / "failed" (validate).

const BASE = "advance/request/import/";

// Import answers 400 when no row was saved but still sends the per-row
// results, so that body is returned instead of thrown.
const postImport = async (domain, path, records, companyId, signal, failText) => {
  const query = companyId ? `?company_id=${encodeURIComponent(companyId)}` : "";
  const response = await fetch(`${domain.replace(/\/$/, "")}/${path}${query}`, {
    method: "POST",
    signal,
    headers: { "Content-Type": "application/json", Authorization: `Token ${localStorage.getItem("token")}` },
    body: JSON.stringify({ records }),
  });
  if (response.status === 401) {
    localStorage.clear();
    window.location.href = "/auth/login";
    throw new Error("Your session has expired. Please sign in again.");
  }
  const data = await response.json().catch(() => null);
  if (Array.isArray(data?.rows)) return data;
  const message = data?.error || data?.detail || data?.message;
  throw new Error(typeof message === "string" && message ? message : `${failText} (${response.status}). Please try again.`);
};

// Saves the valid rows (POST advance/request/import/).
export const importAdvanceRequests = (domain, records, companyId, signal) =>
  postImport(domain, BASE, records, companyId, signal, "Import failed");

// Same checks without saving (POST advance/request/import/validate/).
export const validateAdvanceImport = (domain, records, companyId, signal) =>
  postImport(domain, `${BASE}validate/`, records, companyId, signal, "Validation failed");

// ---- Add Migrated Loan (opening balance from before Cauver) ----
// A loan the employee already had (and was approved) before Cauver. Saved
// through the same POST advance/request/ as Add Loan / Advance, with only
// the fields that call already takes; the backend handles the migration in
// the background. The amount is what is still to be recovered (original −
// already repaid), in equal monthly instalments.
export const toMigratedPayload = (form) => {
  const outstanding = migratedOutstanding(form);
  const count = Number(form.remaining_instalments);
  return {
    user_id: Number(form.user_id),
    type: "Loan",
    advance_type: "Salary",
    amount: outstanding,
    // The form has no description field: the list shows "Opening balance".
    description: "Opening balance",
    reason: form.additional_info.trim() || "Opening balance",
    request_date: form.issue_date,
    repayment_mode: count >= 2 ? "Installment" : "Full",
    repayment_date: moment(form.start_month, "YYYY-MM").startOf("month").format("YYYY-MM-DD"),
    repayment_frequency: "Monthly",
    ...(count >= 2 ? { no_of_installments: count } : {}),
  };
};

export const saveMigratedLoan = (domain, companyId, form, signal) =>
  shiftRequest(domain, withCompany(REQUEST, companyId), { method: "POST", body: toMigratedPayload(form), signal });
